import { useState, useEffect, useRef } from 'react';
import { 
  Bell, 
  Send, 
  Users, 
  Building2, 
  User, 
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Smartphone,
  Monitor,
  Tablet,
  RefreshCw,
  Trash2,
  AlertTriangle,
  BellOff,
  RotateCcw,
  UserX,
  History,
  Smile,
  Link,
  ChevronDown,
  ChevronUp,
  Calendar,
  FileText,
  Plus,
  Edit,
  Save,
  X
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';
import { turkishIncludes } from '../lib/turkishSearch';

export default function SendNotification() {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [firms, setFirms] = useState([]);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [userActions, setUserActions] = useState([]);
  const [actionSummary, setActionSummary] = useState(null);
  
  // Expanded log details
  const [expandedLogs, setExpandedLogs] = useState({});
  
  // Scheduled notifications
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' or 'scheduled'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // ntfy.sh alternative
  const [ntfyInfo, setNtfyInfo] = useState(null);
  const [showNtfyDialog, setShowNtfyDialog] = useState(false);
  const [sendingNtfy, setSendingNtfy] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [notificationUrl, setNotificationUrl] = useState('/dashboard');
  const [targetType, setTargetType] = useState('all');
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Emoji picker state
  const [showTitleEmoji, setShowTitleEmoji] = useState(false);
  const [showBodyEmoji, setShowBodyEmoji] = useState(false);
  const titleInputRef = useRef(null);
  const bodyInputRef = useRef(null);
  
  // Device selection state
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  
  // Action selection state
  const [selectedActions, setSelectedActions] = useState([]);
  const [showDeleteActionsDialog, setShowDeleteActionsDialog] = useState(false);
  const [deletingActions, setDeletingActions] = useState(false);

  useEffect(() => {
    fetchData();
    fetchTemplates();
    fetchScheduledNotifications();
    fetchNtfyInfo();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [firmsRes, devicesRes, logsRes, statsRes, actionsRes] = await Promise.all([
        api.get('/admin/firms'),
        api.get('/push/admin/devices?limit=100'),
        api.get('/push/admin/logs?limit=50'),
        api.get('/push/admin/stats'),
        api.get('/push/admin/user-actions?limit=100')
      ]);
      
      setFirms(firmsRes.data.data || firmsRes.data || []);
      setDevices(devicesRes.data.data || []);
      setLogs(logsRes.data.data || []);
      setStats(statsRes.data.data || null);
      setUserActions(actionsRes.data.data || []);
      setActionSummary(actionsRes.data.summary || null);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchNtfyInfo = async () => {
    try {
      const response = await api.get('/push/admin/ntfy/info');
      if (response.data.success) {
        setNtfyInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ntfy info:', error);
    }
  };

  const handleSendNtfy = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Başlık ve mesaj gereklidir');
      return;
    }
    
    setSendingNtfy(true);
    try {
      const response = await api.post('/push/admin/ntfy/send', {
        title: title,
        body: body,
        click_url: notificationUrl || '/dashboard',
        priority: 4,
        tags: ['bell']
      });
      
      if (response.data.success) {
        toast.success('Bildirim ntfy.sh üzerinden gönderildi', {
          description: `Topic: ${response.data.topic}`
        });
      }
    } catch (error) {
      console.error('ntfy send error:', error);
      toast.error('ntfy.sh gönderimi başarısız');
    } finally {
      setSendingNtfy(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/push/admin/templates');
      // Filter out any templates without name
      const validTemplates = (response.data.data || []).filter(t => t.name && t.name.trim());
      setTemplates(validTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchScheduledNotifications = async () => {
    try {
      const response = await api.get('/push/admin/scheduled');
      setScheduledNotifications(response.data.data || []);
    } catch (error) {
      console.error('Error fetching scheduled notifications:', error);
    }
  };

  const toggleLogExpanded = (logId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !title.trim() || !body.trim()) {
      toast.error('Şablon adı, başlık ve mesaj gereklidir');
      return;
    }
    
    setSavingTemplate(true);
    try {
      const templateData = {
        name: templateName,
        title: title,
        body: body,
        url: notificationUrl
      };
      
      if (editingTemplate) {
        await api.put(`/push/admin/templates/${editingTemplate.id}`, templateData);
        toast.success('Şablon güncellendi');
      } else {
        await api.post('/push/admin/templates', templateData);
        toast.success('Şablon kaydedildi');
      }
      
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      setTemplateName('');
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Şablon kaydedilemedi');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/push/admin/templates/${templateId}`);
      toast.success('Şablon silindi');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Şablon silinemedi');
    }
  };

  const handleUseTemplate = (template) => {
    setTitle(template.title);
    setBody(template.body);
    setNotificationUrl(template.url || '/dashboard');
    toast.success('Şablon uygulandı');
  };

  const handleCancelScheduled = async (scheduledId) => {
    try {
      await api.delete(`/push/admin/scheduled/${scheduledId}`);
      toast.success('Zamanlanmış bildirim iptal edildi');
      fetchScheduledNotifications();
    } catch (error) {
      console.error('Error canceling scheduled notification:', error);
      toast.error('İptal edilemedi');
    }
  };

  const handleResetUserPreference = async () => {
    if (!userToReset) return;
    
    setResetting(true);
    try {
      const response = await api.post(`/push/admin/reset-user-preference/${userToReset.actor_user_id}`);
      
      if (response.data.success) {
        toast.success('Tercih sıfırlandı', {
          description: response.data.message
        });
        fetchData();
      } else {
        toast.error('Tercih sıfırlanamadı');
      }
    } catch (error) {
      console.error('Error resetting preference:', error);
      toast.error('Tercih sıfırlanırken hata oluştu');
    } finally {
      setResetting(false);
      setShowResetDialog(false);
      setUserToReset(null);
    }
  };

  const fetchUsersForFirm = async (firmId) => {
    setLoadingUsers(true);
    try {
      const response = await api.get(`/admin/users?firm_id=${firmId}&status=ACTIVE`);
      const userData = response.data.data || [];
      setUsers(userData);
      setFilteredUsers(userData);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/admin/users?status=ACTIVE&limit=500');
      const userData = response.data.data || [];
      setAllUsers(userData);
      setFilteredUsers(userData);
    } catch (error) {
      console.error('Error fetching all users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSearch = (query) => {
    setUserSearchQuery(query);
    
    const sourceUsers = targetType === 'user' && !selectedFirmId ? allUsers : users;
    
    if (!query.trim()) {
      setFilteredUsers(sourceUsers);
    } else {
      const filtered = sourceUsers.filter(user => 
        turkishIncludes(user.full_name || '', query) ||
        turkishIncludes(user.email || '', query)
      );
      setFilteredUsers(filtered);
    }
  };

  const handleTargetTypeChange = (value) => {
    setTargetType(value);
    setSelectedFirmId('');
    setSelectedUserId('');
    setUsers([]);
    setFilteredUsers([]);
    setUserSearchQuery('');
    
    // "Belirli Kullanıcı" seçildiğinde tüm kullanıcıları getir
    if (value === 'user') {
      fetchAllUsers();
    }
  };

  const handleFirmChange = (firmId) => {
    setSelectedFirmId(firmId);
    setSelectedUserId('');
    setUserSearchQuery('');
    
    if (targetType === 'firm') {
      // Firma seçildiğinde, sadece o firmanın kullanıcılarını göster (bilgi amaçlı)
      fetchUsersForFirm(firmId);
    } else if (targetType === 'user') {
      // Kullanıcı hedefi için firma filtresi
      fetchUsersForFirm(firmId);
    }
  };

  const handleUserChange = (userId) => {
    setSelectedUserId(userId);
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Başlık ve mesaj zorunludur');
      return;
    }

    // Validate scheduled time
    if (scheduleMode === 'scheduled') {
      if (!scheduledDate || !scheduledTime) {
        toast.error('Zamanlanmış gönderim için tarih ve saat seçmelisiniz');
        return;
      }
      
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        toast.error('Zamanlanmış tarih gelecekte olmalıdır');
        return;
      }
    }

    // Determine target_id based on target type
    let targetId = null;
    if (targetType === 'firm') {
      targetId = selectedFirmId;
    } else if (targetType === 'user') {
      targetId = selectedUserId;
    }

    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        body: body.trim(),
        target_type: targetType,
        target_id: targetId,
        url: notificationUrl || '/dashboard'
      };

      let response;
      if (scheduleMode === 'scheduled') {
        // Schedule for later - Convert local time to ISO string with timezone
        // Create date from local inputs and convert to ISO string
        const localDateTime = new Date(`${scheduledDate}T${scheduledTime}:00`);
        payload.scheduled_at = localDateTime.toISOString();
        response = await api.post('/push/admin/schedule', payload);
        
        if (response.data.success) {
          toast.success('Bildirim zamanlandı', {
            description: `${scheduledDate} ${scheduledTime} tarihinde gönderilecek`
          });
          setScheduledDate('');
          setScheduledTime('');
          setScheduleMode('now');
          fetchScheduledNotifications();
        }
      } else {
        // Send immediately
        response = await api.post('/push/admin/send', payload);
        
        if (response.data.success) {
          toast.success(`Bildirim gönderildi`, {
            description: `${response.data.sent} cihaza, ${response.data.users_notified || 0} kullanıcıya gönderildi`
          });
        }
      }

      if (response.data.success) {
        setTitle('');
        setBody('');
        setNotificationUrl('/dashboard');
        setTargetType('all');
        setSelectedFirmId('');
        setSelectedUserId('');
        fetchData();
      } else {
        toast.error('İşlem başarısız', {
          description: response.data.message
        });
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('İşlem sırasında hata oluştu');
    } finally {
      setSending(false);
    }
  };

  // Emoji handlers
  const handleTitleEmojiClick = (emojiData) => {
    setTitle(prev => prev + emojiData.emoji);
    setShowTitleEmoji(false);
  };

  const handleBodyEmojiClick = (emojiData) => {
    setBody(prev => prev + emojiData.emoji);
    setShowBodyEmoji(false);
  };

  // Device selection handlers
  const handleSelectDevice = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAllDevices = () => {
    if (selectedDevices.length === devices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(devices.map(d => d.id));
    }
  };

  const handleDeleteDevices = async () => {
    if (selectedDevices.length === 0) return;
    
    setDeleting(true);
    try {
      const queryParams = selectedDevices.map(id => `device_ids=${id}`).join('&');
      const response = await api.delete(`/push/admin/devices?${queryParams}`);
      
      if (response.data.success) {
        toast.success(`${response.data.deleted_count} cihaz silindi`);
        setSelectedDevices([]);
        fetchData();
      } else {
        toast.error('Cihazlar silinemedi');
      }
    } catch (error) {
      console.error('Error deleting devices:', error);
      toast.error('Cihazlar silinirken hata oluştu');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Action selection handlers
  const handleSelectAction = (actionId) => {
    setSelectedActions(prev => 
      prev.includes(actionId) 
        ? prev.filter(id => id !== actionId)
        : [...prev, actionId]
    );
  };

  const handleSelectAllActions = () => {
    if (selectedActions.length === userActions.length) {
      setSelectedActions([]);
    } else {
      setSelectedActions(userActions.map(a => a.id));
    }
  };

  const handleDeleteActions = async () => {
    if (selectedActions.length === 0) return;
    
    setDeletingActions(true);
    try {
      const queryParams = selectedActions.map(id => `log_ids=${id}`).join('&');
      const response = await api.delete(`/push/admin/user-actions?${queryParams}`);
      
      if (response.data.success) {
        toast.success(`${response.data.deleted_count} aksiyon kaydı silindi`);
        setSelectedActions([]);
        fetchData();
      } else {
        toast.error('Kayıtlar silinemedi');
      }
    } catch (error) {
      console.error('Error deleting actions:', error);
      toast.error('Kayıtlar silinirken hata oluştu');
    } finally {
      setDeletingActions(false);
      setShowDeleteActionsDialog(false);
    }
  };

  const handleDeleteSingleAction = async (actionId) => {
    try {
      const response = await api.delete(`/push/admin/user-actions/${actionId}`);
      
      if (response.data.success) {
        toast.success('Aksiyon kaydı silindi');
        fetchData();
      } else {
        toast.error('Kayıt silinemedi');
      }
    } catch (error) {
      console.error('Error deleting action:', error);
      toast.error('Kayıt silinirken hata oluştu');
    }
  };

  const handleDeleteSingleDevice = async (deviceId) => {
    try {
      const response = await api.delete(`/push/admin/devices/${deviceId}`);
      
      if (response.data.success) {
        toast.success('Cihaz silindi');
        fetchData();
      } else {
        toast.error('Cihaz silinemedi');
      }
    } catch (error) {
      console.error('Error deleting device:', error);
      toast.error('Cihaz silinirken hata oluştu');
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile': return <Smartphone className="h-4 w-4" />;
      case 'tablet': return <Tablet className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get action info for display
  const getActionInfo = (action) => {
    switch (action) {
      case 'push_notification_dismissed':
        return { 
          label: 'Hayır Dedi', 
          color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
          icon: <BellOff className="h-4 w-4" />
        };
      case 'push_notification_remind_later':
        return { 
          label: 'Sonra Hatırlat', 
          color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
          icon: <Clock className="h-4 w-4" />
        };
      case 'push_notification_enabled':
        return { 
          label: 'Etkinleştirdi', 
          color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          icon: <CheckCircle2 className="h-4 w-4" />
        };
      case 'push_notification_failed':
        return { 
          label: 'Başarısız', 
          color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
          icon: <XCircle className="h-4 w-4" />
        };
      default:
        return { 
          label: action, 
          color: 'bg-slate-100 text-slate-700',
          icon: null
        };
    }
  };

  // Get display text for target selection
  const getTargetDisplayText = () => {
    if (targetType === 'all') return 'Tüm Kullanıcılar';
    if (targetType === 'firm') return 'Belirli Firma';
    if (targetType === 'user') return 'Belirli Kullanıcı';
    return 'Seçiniz';
  };

  const getSelectedFirmName = () => {
    const firm = firms.find(f => f.id === selectedFirmId);
    return firm?.name || 'Firma seçin...';
  };

  const getSelectedUserName = () => {
    // Önce users'da ara, sonra allUsers'da ara
    let user = users.find(u => u.id === selectedUserId);
    if (!user) {
      user = allUsers.find(u => u.id === selectedUserId);
    }
    if (!user) {
      user = filteredUsers.find(u => u.id === selectedUserId);
    }
    return user ? `${user.full_name} (${user.email})` : 'Kullanıcı seçin...';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6 text-blue-600" />
            Bildirim Gönder
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Web push bildirimleri gönderin
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                  <Smartphone className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_subscriptions}</p>
                  <p className="text-xs text-slate-500">Kayıtlı Cihaz</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <Send className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_notifications_sent}</p>
                  <p className="text-xs text-slate-500">Gönderilen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                  <Monitor className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.device_stats?.desktop || 0}</p>
                  <p className="text-xs text-slate-500">Masaüstü</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                  <Smartphone className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.device_stats?.mobile || 0}</p>
                  <p className="text-xs text-slate-500">Mobil</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="send" className="space-y-4">
        <TabsList>
          <TabsTrigger value="send">Bildirim Gönder</TabsTrigger>
          <TabsTrigger value="devices">Kayıtlı Cihazlar ({devices.length})</TabsTrigger>
          <TabsTrigger value="actions">Kullanıcı Aksiyonları</TabsTrigger>
          <TabsTrigger value="logs">Gönderim Geçmişi</TabsTrigger>
        </TabsList>

        {/* Send Notification Tab */}
        <TabsContent value="send">
          <Card>
            <CardHeader>
              <CardTitle>Yeni Bildirim</CardTitle>
              <CardDescription>
                Kullanıcılara web push bildirimi gönderin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hedef</Label>
                  <Select value={targetType} onValueChange={handleTargetTypeChange}>
                    <SelectTrigger>
                      <SelectValue>{getTargetDisplayText()}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Tüm Kullanıcılar
                        </div>
                      </SelectItem>
                      <SelectItem value="firm">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          Belirli Firma
                        </div>
                      </SelectItem>
                      <SelectItem value="user">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Belirli Kullanıcı
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {targetType === 'firm' && (
                  <div className="space-y-2">
                    <Label>Firma Seçin *</Label>
                    <Select value={selectedFirmId} onValueChange={handleFirmChange}>
                      <SelectTrigger>
                        <SelectValue>{selectedFirmId ? getSelectedFirmName() : 'Firma seçin...'}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {firms.map(firm => (
                          <SelectItem key={firm.id} value={firm.id}>
                            {firm.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedFirmId && users.length > 0 && (
                      <p className="text-xs text-slate-500">
                        Bu firmada {users.length} aktif kullanıcı var
                      </p>
                    )}
                  </div>
                )}

                {targetType === 'user' && (
                  <div className="space-y-2">
                    <Label>Firma Filtresi (opsiyonel)</Label>
                    <Select value={selectedFirmId || "all_firms"} onValueChange={(val) => handleFirmChange(val === "all_firms" ? "" : val)}>
                      <SelectTrigger>
                        <SelectValue>{selectedFirmId ? getSelectedFirmName() : 'Tüm firmalar...'}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_firms">Tüm Firmalar</SelectItem>
                        {firms.map(firm => (
                          <SelectItem key={firm.id} value={firm.id}>
                            {firm.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* User Search and Selection - Only for "Belirli Kullanıcı" target */}
              {targetType === 'user' && (
                <div className="space-y-3 p-4 border rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <Label className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Kullanıcı Ara ve Seç *
                  </Label>
                  <Input
                    placeholder="İsim veya email ile ara..."
                    value={userSearchQuery}
                    onChange={(e) => handleUserSearch(e.target.value)}
                    className="bg-white dark:bg-slate-900"
                  />
                  
                  {loadingUsers ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                      <span className="ml-2 text-sm text-slate-500">Kullanıcılar yükleniyor...</span>
                    </div>
                  ) : filteredUsers.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto border rounded-md bg-white dark:bg-slate-900">
                      {filteredUsers.slice(0, 50).map(user => (
                        <div
                          key={user.id}
                          onClick={() => {
                            setSelectedUserId(user.id);
                            setUserSearchQuery(user.full_name);
                          }}
                          className={`
                            px-3 py-2 cursor-pointer transition-colors border-b last:border-b-0
                            ${selectedUserId === user.id 
                              ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200' 
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800'}
                          `}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{user.full_name}</p>
                              <p className="text-xs text-slate-500">{user.email}</p>
                            </div>
                            {selectedUserId === user.id && (
                              <CheckCircle2 className="h-4 w-4 text-blue-500" />
                            )}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {user.firm_name || 'Firma belirtilmemiş'}
                          </p>
                        </div>
                      ))}
                      {filteredUsers.length > 50 && (
                        <div className="px-3 py-2 text-xs text-slate-500 text-center bg-slate-50">
                          +{filteredUsers.length - 50} daha fazla sonuç - aramayı daraltın
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-400 text-sm">
                      {userSearchQuery ? 'Kullanıcı bulunamadı' : 'Aramaya başlayın...'}
                    </div>
                  )}
                  
                  {selectedUserId && (
                    <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded-md">
                      <CheckCircle2 className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium">Seçili: {getSelectedUserName()}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto h-6 px-2"
                        onClick={() => {
                          setSelectedUserId('');
                          setUserSearchQuery('');
                        }}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Başlık *
                  <Popover open={showTitleEmoji} onOpenChange={setShowTitleEmoji}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0"
                        type="button"
                      >
                        <Smile className="h-4 w-4 text-slate-400 hover:text-amber-500" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <EmojiPicker 
                        onEmojiClick={handleTitleEmojiClick}
                        width={300}
                        height={350}
                        searchPlaceholder="Emoji ara..."
                        previewConfig={{ showPreview: false }}
                      />
                    </PopoverContent>
                  </Popover>
                </Label>
                <Input
                  ref={titleInputRef}
                  placeholder="Bildirim başlığı 🔔"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={100}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Mesaj *
                  <Popover open={showBodyEmoji} onOpenChange={setShowBodyEmoji}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0"
                        type="button"
                      >
                        <Smile className="h-4 w-4 text-slate-400 hover:text-amber-500" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <EmojiPicker 
                        onEmojiClick={handleBodyEmojiClick}
                        width={300}
                        height={350}
                        searchPlaceholder="Emoji ara..."
                        previewConfig={{ showPreview: false }}
                      />
                    </PopoverContent>
                  </Popover>
                </Label>
                <Textarea
                  ref={bodyInputRef}
                  placeholder="Bildirim mesajı... 📢"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  maxLength={500}
                />
                <p className="text-xs text-slate-500 text-right">{body.length}/500</p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Link className="h-4 w-4" />
                  Tıklama URL'si (opsiyonel)
                </Label>
                <Input
                  placeholder="/dashboard veya tam URL (örn: https://site.com/page)"
                  value={notificationUrl}
                  onChange={(e) => setNotificationUrl(e.target.value)}
                  data-testid="notification-url-input"
                />
                <p className="text-xs text-slate-500">
                  Kullanıcı bildirime tıkladığında yönlendirileceği sayfa
                </p>
              </div>

              {/* Scheduling Options */}
              <div className="space-y-3 p-4 border rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Gönderim Zamanı
                </Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scheduleMode"
                      value="now"
                      checked={scheduleMode === 'now'}
                      onChange={() => setScheduleMode('now')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">Şimdi Gönder</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scheduleMode"
                      value="scheduled"
                      checked={scheduleMode === 'scheduled'}
                      onChange={() => setScheduleMode('scheduled')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">Zamanla</span>
                  </label>
                </div>
                
                {scheduleMode === 'scheduled' && (
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500 mb-1 block">Tarih</Label>
                      <Input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500 mb-1 block">Saat</Label>
                      <Input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="bg-white dark:bg-slate-900"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Templates */}
              <div className="space-y-3 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Şablonlar
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingTemplate(null);
                      setTemplateName('');
                      setShowTemplateDialog(true);
                    }}
                    disabled={!title.trim() || !body.trim()}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Şablon Kaydet
                  </Button>
                </div>
                
                {templates.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {templates.map(template => (
                      <div
                        key={template.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-full text-sm group"
                      >
                        <button
                          onClick={() => handleUseTemplate(template)}
                          className="hover:text-blue-600 transition-colors"
                          title="Şablonu Kullan"
                        >
                          {template.name}
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-all ml-1"
                          title="Şablonu Sil"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">
                    Henüz şablon yok. Formu doldurup "Şablon Kaydet" ile kaydedin.
                  </p>
                )}
              </div>

              {/* Scheduled Notifications List */}
              {scheduledNotifications.length > 0 && (
                <div className="space-y-2 p-4 border border-amber-200 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                  <Label className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <Calendar className="h-4 w-4" />
                    Zamanlanmış Bildirimler ({scheduledNotifications.length})
                  </Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {scheduledNotifications.map(sn => (
                      <div key={sn.id} className="flex items-center justify-between text-sm bg-white dark:bg-slate-800 rounded p-2">
                        <div>
                          <p className="font-medium">{sn.title}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(sn.scheduled_at).toLocaleString('tr-TR')}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleCancelScheduled(sn.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                {/* ntfy.sh Alternative Button */}
                <Button 
                  variant="outline"
                  onClick={() => setShowNtfyDialog(true)}
                  disabled={!title.trim() || !body.trim()}
                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                  title="ntfy.sh ile gönder (Web Push alternatifi)"
                >
                  <Bell className="h-4 w-4 mr-2" />
                  ntfy.sh
                </Button>
                
                <Button 
                  onClick={handleSend} 
                  disabled={sending || !title.trim() || !body.trim() || (scheduleMode === 'scheduled' && (!scheduledDate || !scheduledTime))}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {scheduleMode === 'scheduled' ? 'Zamanlanıyor...' : 'Gönderiliyor...'}
                    </>
                  ) : (
                    <>
                      {scheduleMode === 'scheduled' ? (
                        <>
                          <Calendar className="h-4 w-4 mr-2" />
                          Zamanla
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Bildirim Gönder
                        </>
                      )}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Devices Tab */}
        <TabsContent value="devices">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Kayıtlı Cihazlar</CardTitle>
                  <CardDescription>
                    Bildirim almak için kayıt olmuş cihazlar
                  </CardDescription>
                </div>
                {selectedDevices.length > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={deleting}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {selectedDevices.length} Cihaz Sil
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={devices.length > 0 && selectedDevices.length === devices.length}
                        onCheckedChange={handleSelectAllDevices}
                      />
                    </TableHead>
                    <TableHead>Kullanıcı</TableHead>
                    <TableHead>Firma</TableHead>
                    <TableHead>Cihaz</TableHead>
                    <TableHead>Tarayıcı</TableHead>
                    <TableHead>İşletim Sistemi</TableHead>
                    <TableHead>Kayıt Tarihi</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                        Henüz kayıtlı cihaz yok
                      </TableCell>
                    </TableRow>
                  ) : (
                    devices.map(device => (
                      <TableRow 
                        key={device.id}
                        className={selectedDevices.includes(device.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                      >
                        <TableCell>
                          <Checkbox 
                            checked={selectedDevices.includes(device.id)}
                            onCheckedChange={() => handleSelectDevice(device.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{device.user_name || '-'}</p>
                            <p className="text-xs text-slate-500">{device.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{device.firm_name || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getDeviceIcon(device.device_type)}
                            <span className="capitalize">{device.device_type || 'Desktop'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{device.browser || '-'}</Badge>
                        </TableCell>
                        <TableCell>{device.os || '-'}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {formatDate(device.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-600"
                            onClick={() => handleDeleteSingleDevice(device.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Actions Tab */}
        <TabsContent value="actions">
          <div className="space-y-4">
            {/* Action Summary Cards */}
            {actionSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{actionSummary.enabled || 0}</p>
                        <p className="text-xs text-slate-500">Etkinleştirdi</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900 flex items-center justify-center">
                        <BellOff className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{actionSummary.dismissed || 0}</p>
                        <p className="text-xs text-slate-500">Hayır Dedi</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{actionSummary.remind_later || 0}</p>
                        <p className="text-xs text-slate-500">Sonra Hatırlat</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                        <XCircle className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{actionSummary.failed || 0}</p>
                        <p className="text-xs text-slate-500">Başarısız</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <History className="h-5 w-5" />
                      Kullanıcı Aksiyonları
                    </CardTitle>
                    <CardDescription>
                      Bildirim izni istenen kullanıcıların verdikleri yanıtlar.
                      "Hayır" veya "Sonra" diyenlerin tercihini sıfırlayabilirsiniz.
                    </CardDescription>
                  </div>
                  {selectedActions.length > 0 && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => setShowDeleteActionsDialog(true)}
                      disabled={deletingActions}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {selectedActions.length} Kayıt Sil
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox 
                          checked={userActions.length > 0 && selectedActions.length === userActions.length}
                          onCheckedChange={handleSelectAllActions}
                        />
                      </TableHead>
                      <TableHead>Kullanıcı</TableHead>
                      <TableHead>Firma</TableHead>
                      <TableHead>Aksiyon</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead className="w-32">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userActions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                          Henüz kayıtlı aksiyon yok
                        </TableCell>
                      </TableRow>
                    ) : (
                      userActions.map((action, index) => {
                        const actionInfo = getActionInfo(action.action);
                        const canReset = action.action === 'push_notification_dismissed' || 
                                        action.action === 'push_notification_remind_later';
                        
                        return (
                          <TableRow 
                            key={`${action.actor_user_id}-${index}`}
                            className={selectedActions.includes(action.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                          >
                            <TableCell>
                              <Checkbox 
                                checked={selectedActions.includes(action.id)}
                                onCheckedChange={() => handleSelectAction(action.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{action.user_name || '-'}</p>
                                <p className="text-xs text-slate-500">{action.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{action.firm_name || '-'}</TableCell>
                            <TableCell>
                              <Badge className={actionInfo.color}>
                                <span className="flex items-center gap-1">
                                  {actionInfo.icon}
                                  {actionInfo.label}
                                </span>
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {formatDate(action.created_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {canReset && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setUserToReset(action);
                                      setShowResetDialog(true);
                                    }}
                                    className="h-8 text-xs"
                                  >
                                    <RotateCcw className="h-3 w-3 mr-1" />
                                    Sıfırla
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-slate-400 hover:text-red-600"
                                  onClick={() => handleDeleteSingleAction(action.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Gönderim Geçmişi</CardTitle>
              <CardDescription>
                Son gönderilen bildirimler - ayrıntı için tıklayın
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {logs.length === 0 ? (
                  <div className="text-center text-slate-400 py-8">
                    Henüz gönderim yok
                  </div>
                ) : (
                  logs.map(log => (
                    <div 
                      key={log.id} 
                      className="border rounded-lg overflow-hidden"
                    >
                      {/* Header - Always Visible */}
                      <div 
                        className={`p-4 cursor-pointer transition-colors ${log.failed_count > 0 ? 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30' : 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'}`}
                        onClick={() => toggleLogExpanded(log.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold truncate">{log.title}</h4>
                              <Badge variant="outline" className="text-xs flex-shrink-0">
                                {log.target_type === 'all' ? 'Tümü' : 
                                 log.target_type === 'firm' ? 'Firma' : 'Kullanıcı'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500 truncate">{log.body}</p>
                            <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(log.created_at)}
                              </span>
                              <span className="hidden sm:inline">Gönderen: {log.admin_name}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                            <div className="text-center px-2 py-1 bg-green-100 dark:bg-green-800 rounded">
                              <span className="flex items-center gap-1 text-green-700 dark:text-green-300 font-semibold text-sm">
                                <CheckCircle2 className="h-4 w-4" />
                                {log.sent_count}
                              </span>
                            </div>
                            <div className={`text-center px-2 py-1 rounded ${log.failed_count > 0 ? 'bg-red-100 dark:bg-red-800' : 'bg-slate-100 dark:bg-slate-700'}`}>
                              <span className={`flex items-center gap-1 font-semibold text-sm ${log.failed_count > 0 ? 'text-red-700 dark:text-red-300' : 'text-slate-400'}`}>
                                <XCircle className="h-4 w-4" />
                                {log.failed_count || 0}
                              </span>
                            </div>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              {expandedLogs[log.id] ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* User Details - Expandable */}
                      {expandedLogs[log.id] && (
                        <div className="p-4 bg-white dark:bg-slate-900 border-t">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Success List */}
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                                  Bildirim Giden Kullanıcılar ({log.sent_count})
                                </span>
                              </div>
                              {log.success_details && log.success_details.length > 0 ? (
                                <div className="space-y-1 max-h-48 overflow-y-auto border rounded-md p-2 bg-green-50/50 dark:bg-green-900/10">
                                  {log.success_details.map((detail, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-xs p-1.5 bg-white dark:bg-slate-800 rounded border border-green-100 dark:border-green-900">
                                      <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                                      <div className="min-w-0 flex-1">
                                        <span className="font-medium text-slate-700 dark:text-slate-300 block truncate">
                                          {detail.user_name || detail.user_email || 'Bilinmeyen'}
                                        </span>
                                        {detail.user_name && detail.user_email && (
                                          <span className="text-slate-400 text-[10px] block truncate">{detail.user_email}</span>
                                        )}
                                      </div>
                                      <Badge variant="secondary" className="text-[10px] flex-shrink-0">
                                        {detail.device_type || 'desktop'}
                                      </Badge>
                                    </div>
                                  ))}
                                  {log.success_details.length < log.sent_count && (
                                    <p className="text-xs text-slate-400 text-center py-1">
                                      +{log.sent_count - log.success_details.length} daha fazla
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div className="text-xs text-slate-400 p-2 border rounded-md bg-slate-50 dark:bg-slate-800">
                                  {log.sent_count > 0 ? 'Detay kaydedilmemiş (eski kayıt)' : 'Başarılı gönderim yok'}
                                </div>
                              )}
                            </div>

                            {/* Failed List */}
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <XCircle className="h-4 w-4 text-red-500" />
                                <span className="text-sm font-medium text-red-700 dark:text-red-400">
                                  Bildirim Gitmeyen Kullanıcılar ({log.failed_count || 0})
                                </span>
                              </div>
                              {log.failed_details && log.failed_details.length > 0 ? (
                                <div className="space-y-1 max-h-48 overflow-y-auto border rounded-md p-2 bg-red-50/50 dark:bg-red-900/10">
                                  {log.failed_details.map((detail, idx) => (
                                    <div key={idx} className="text-xs p-1.5 bg-white dark:bg-slate-800 rounded border border-red-100 dark:border-red-900">
                                      <div className="flex items-center gap-2">
                                        <XCircle className="h-3 w-3 text-red-500 flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                          <span className="font-medium text-slate-700 dark:text-slate-300 block truncate">
                                            {detail.user_name || detail.user_email || 'Bilinmeyen'}
                                          </span>
                                          {detail.user_name && detail.user_email && (
                                            <span className="text-slate-400 text-[10px] block truncate">{detail.user_email}</span>
                                          )}
                                        </div>
                                        {detail.status_code && (
                                          <Badge variant="destructive" className="text-[10px] flex-shrink-0">
                                            {detail.status_code}
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-red-500 text-[10px] mt-1 truncate pl-5" title={detail.error}>
                                        {detail.error}
                                      </p>
                                    </div>
                                  ))}
                                  {log.failed_details.length < (log.failed_count || 0) && (
                                    <p className="text-xs text-slate-400 text-center py-1">
                                      +{(log.failed_count || 0) - log.failed_details.length} daha fazla
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div className="text-xs text-slate-400 p-2 border rounded-md bg-slate-50 dark:bg-slate-800">
                                  {log.failed_count > 0 ? 'Detay kaydedilmemiş (eski kayıt)' : 'Tüm bildirimler başarıyla gönderildi ✓'}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Cihazları Sil
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedDevices.length} cihazı silmek istediğinize emin misiniz? 
              Bu işlem geri alınamaz ve silinen cihazlara bildirim gönderilemez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDevices}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Sil
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Preference Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-blue-500" />
              Tercihi Sıfırla
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{userToReset?.user_name}</strong> için bildirim tercihini sıfırlamak istediğinize emin misiniz?
              <br /><br />
              Bu kullanıcı bir sonraki girişinde tekrar bildirim izni istenecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetUserPreference}
              disabled={resetting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {resetting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sıfırlanıyor...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Sıfırla
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Actions Dialog */}
      <AlertDialog open={showDeleteActionsDialog} onOpenChange={setShowDeleteActionsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Aksiyon Kayıtlarını Sil
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedActions.length} aksiyon kaydını silmek istediğinize emin misiniz? 
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteActions}
              disabled={deletingActions}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingActions ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Sil
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template Save Dialog */}
      <AlertDialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              {editingTemplate ? 'Şablonu Düzenle' : 'Şablon Kaydet'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Bu bildirimi şablon olarak kaydedin ve tekrar kullanın.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label className="text-sm mb-2 block">Şablon Adı</Label>
            <Input
              placeholder="Örn: Haftalık Duyuru, Acil Bildirim..."
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              autoFocus
            />
            <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-md text-sm">
              <p className="font-medium text-slate-700 dark:text-slate-300">{title || 'Başlık'}</p>
              <p className="text-slate-500 text-xs mt-1">{body || 'Mesaj içeriği'}</p>
              {notificationUrl && notificationUrl !== '/dashboard' && (
                <p className="text-blue-500 text-xs mt-1">URL: {notificationUrl}</p>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowTemplateDialog(false);
              setEditingTemplate(null);
              setTemplateName('');
            }}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSaveTemplate}
              disabled={savingTemplate || !templateName.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingTemplate ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Kaydet
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ntfy.sh Dialog */}
      <AlertDialog open={showNtfyDialog} onOpenChange={setShowNtfyDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-orange-500" />
              ntfy.sh ile Bildirim Gönder
            </AlertDialogTitle>
            <AlertDialogDescription>
              Web Push'a alternatif olarak ntfy.sh servisini kullanabilirsiniz. 
              Bu servis ücretsizdir ve mobil/masaüstü uygulamaları mevcuttur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200">
              <p className="font-medium text-sm mb-2">Nasıl Çalışır?</p>
              <ol className="text-xs text-slate-600 dark:text-slate-400 space-y-1 list-decimal list-inside">
                <li>ntfy.sh uygulamasını telefonunuza indirin (Android/iOS)</li>
                <li>
                  <strong>{ntfyInfo?.topic || 'hr-portal-notifications'}</strong> konusuna abone olun
                </li>
                <li>Artık bu sayfadan gönderilen bildirimler size ulaşacak!</li>
              </ol>
            </div>
            
            {ntfyInfo && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Abone Ol:</p>
                <div className="flex flex-wrap gap-2">
                  <a 
                    href={ntfyInfo.android_app}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs hover:bg-green-200 transition-colors"
                  >
                    <Smartphone className="h-3 w-3 mr-1" />
                    Android
                  </a>
                  <a 
                    href={ntfyInfo.ios_app}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs hover:bg-blue-200 transition-colors"
                  >
                    <Smartphone className="h-3 w-3 mr-1" />
                    iOS
                  </a>
                  <a 
                    href={ntfyInfo.web_app}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-xs hover:bg-purple-200 transition-colors"
                  >
                    <Monitor className="h-3 w-3 mr-1" />
                    Web
                  </a>
                </div>
              </div>
            )}
            
            <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Gönderilecek Bildirim:</p>
              <p className="font-medium text-sm">{title || '(Başlık yok)'}</p>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{body || '(Mesaj yok)'}</p>
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleSendNtfy();
                setShowNtfyDialog(false);
              }}
              disabled={sendingNtfy || !title.trim() || !body.trim()}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {sendingNtfy ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  ntfy.sh ile Gönder
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
